import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { combineLatest, of, throwError, } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import {
  AppReasonService,
  AgnPresenceTypeService,
  AppStateService,
  VersionService,
  LocationService,
  DepartmentService,
  StockService,
  ServiceArticleTreatmentService,
  FirmService,
  MhInitializationService,
  UserProfileService,
  AnimalTypesService,
  HexService,
  TimeRangeConfigsService,
  CountryService,
  PostAddressService,
  AgnBookService,
  TranslationService,
  CustomerExternalNumberService,
  AnimalExternalNumberService,
  AnimalDateTypeService,
  CustomerCategoryGroupService,
  CustomerCategoryService
} from 'src/app/api/services';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';

import * as fromReasonActions from '../actions/static-data.action';
import * as fromStaticDataSelectors from '../selectors/static-data.selector';
import { RgTextList } from 'src/app/medical-history/models/rg-text-list.model';
import { SalesArticleTreatmentServicesType } from 'src/app/medical-history/enums/salesArticle-treatment-services-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { AppState } from '../states/app.state';

@Injectable()
export class StaticDataEffect {
  constructor(
    private actions$: Actions,
    private reasonService: AppReasonService,
    private presenceTypeService: AgnPresenceTypeService,
    private appStateService: AppStateService,
    private versionService: VersionService,
    private localStorageService: LocalStorageService,
    private locationService: LocationService,
    private departmentService: DepartmentService,
    private stockService: StockService,
    private serviceArticleTreatmentService: ServiceArticleTreatmentService,
    private firmService: FirmService,
    private mhInitialization: MhInitializationService,
    private userProfileService: UserProfileService,
    private animalTypesService: AnimalTypesService,
    private readonly diagnosesService: HexService,
    private hexService: HexService,
    private readonly timeRangeConfigsService: TimeRangeConfigsService,
    private readonly countryService: CountryService,
    private readonly postAddressService: PostAddressService,
    private readonly bookService: AgnBookService,
    private readonly translationService: TranslationService,
    private readonly customerExternalNumberService: CustomerExternalNumberService,
    private readonly animalExternalNumberService: AnimalExternalNumberService,
    private readonly animalDateTypeService: AnimalDateTypeService,
    private store$: Store<AppState>,
    private customerCategoryGroupService: CustomerCategoryGroupService,
    private customerCategoryService: CustomerCategoryService,
  ) { }

  loadAppointmentStaticData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadStaticData),
      switchMap(() => {
        return combineLatest([
          this.reasonService.findAll13(),
          this.presenceTypeService.findAll18(),
          this.appStateService.getAllStates(),
          this.userProfileService.getUserConfig(),
          this.timeRangeConfigsService.findAll()
        ]).pipe(
          map(([reasons, presenceTypes, appointmentStates, userConfig, timeRangeConfigs]) =>
            fromReasonActions.loadStaticDataSuccess({
              reasons,
              presenceTypes,
              appointmentStates,
              userConfig,
              timeRangeConfigs
            })
          )
        );
      })
    )
  );

  loadMedicalHistoryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadMedicalHistoryData),
      switchMap(() => {
        return combineLatest([
          this.departmentService.findAll5(),
          this.stockService.findAll1(),
          this.firmService.getFirms(),
          this.mhInitialization.getInitializationByUserId()
            .pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                  return of(undefined)
                } else {
                  return throwError(error);
                }
              })
            )
        ]).pipe(
          map(([departments, inventoryList, firms, mhInitialization]) =>
            fromReasonActions.loadMedicalHistoryDataSuccess({
              departments,
              inventoryList,
              firms,
              mhInitialization
            })
          )
        );
      })
    )
  );

  loadHexData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadHexData),
      switchMap(() => {
        return combineLatest([
          this.hexService.findAll4(),
          this.hexService.findAll3()
            .pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                  return of(undefined)
                } else {
                  return throwError(error);
                }
              })
            )
        ]).pipe(
          map(([applicationTypes, servingModes]) =>
            fromReasonActions.loadHexDataSuccess({
              applicationTypes,
              servingModes
            })
          )
        );
      })
    )
  );

  loadServiceArticleTreatmentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadServiceArticleTreatmentData),
      switchMap(() => {
        return combineLatest([
          this.serviceArticleTreatmentService.getServiceArticleTreatment(),
        ]).pipe(
          map(([serviceArticleTreatments]) => {
            const rgTextOptions: RgTextList[] = [];
            if (serviceArticleTreatments.salesArticles) {
              serviceArticleTreatments.salesArticles.map(saleArticle => {
                const articleRgText: RgTextList = {
                  description: saleArticle.salesArticleText,
                  searchText: saleArticle.searchAcronym?.trim(),
                  vatRate: saleArticle.vatRate,
                  salesArticleTreatementType:
                    SalesArticleTreatmentServicesType.SalesArticle,
                  capacity: saleArticle.unitShortName,
                  id: saleArticle.id,
                  salesArticle: saleArticle
                };
                rgTextOptions.push(articleRgText);
              });
            }
            if (serviceArticleTreatments.services) {
              serviceArticleTreatments.services.map(service => {
                const serviceRgText: RgTextList = {
                  description: service.serviceText,
                  searchText: service.searchAcronym?.trim(),
                  vatRate: service.vatRate,
                  salesArticleTreatementType:
                    SalesArticleTreatmentServicesType.Services,
                  id: service.id,
                  service: service
                };
                rgTextOptions.push(serviceRgText);
              });
            }
            if (serviceArticleTreatments.stdTreatments) {
              serviceArticleTreatments.stdTreatments.map(stdTreatment => {
                const treatmentRgText: RgTextList = {
                  description: stdTreatment.name,
                  searchText: stdTreatment.suchText?.trim(),
                  salesArticleTreatementType: SalesArticleTreatmentServicesType.StdTreatement,
                  id: stdTreatment.id,
                  stdTreatment: stdTreatment
                }
                rgTextOptions.push(treatmentRgText);
              });
            }
            rgTextOptions.sort((a, b) => {
              if (a.searchText && b.searchText) {
                return a.searchText.trim().localeCompare(b.searchText.trim());
              }
              return -1;
            });

            return fromReasonActions.loadServiceArticleTreatmentDataSuccess({
              serviceArticleTreatments: rgTextOptions
            })
          })
        );
      })
    )
  );

  loadSpeciesDiagnosisData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadSpeciesDiagnosisData),
      switchMap(() => {
        return combineLatest([
          this.diagnosesService.getDiagnoses(),
        ]).pipe(
          map(([allDiagnosis]) => {

            return fromReasonActions.loadSpeciesDiagnosisDataSuccess({
              speciesDiagnoses: allDiagnosis,
            })
          })
        );
      })
    )
  );

  loadVersionData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadVersion),
      switchMap(() => {
        return combineLatest([this.versionService.getVersion()]).pipe(
          map(([version]) => {
            this.localStorageService.setVersion(version);
            return fromReasonActions.loadVersionSuccess({ version });
          })
        );
      })
    )
  );

  loadAnimalTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadAnimalTypes),
      switchMap(() => {
        return combineLatest([this.animalTypesService.findAll15()]).pipe(
          map(([animalTypes]) => {
            return fromReasonActions.loadAnimalTypesSuccess({ animalTypes });
          })
        );
      })
    )
  );

  loadFirms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadFirms),
      switchMap(() => {
        return combineLatest([this.firmService.getFirms()]).pipe(
          map(([firms]) => {
            return fromReasonActions.loadFirmsSuccess({ firms });
          })
        );
      })
    )
  );

  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadLocations),
      switchMap(() => {
        return combineLatest([this.locationService.findLocations()]).pipe(
          map(([locations]) => {
            return fromReasonActions.loadLocationsSuccess({ locations });
          })
        );
      })
    )
  );

  loadCountryList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadCountryList),
      switchMap(() => {
        return combineLatest([this.countryService.findAll9()]).pipe(
          map(([countryList]) => {
            return fromReasonActions.loadCountryListSuccess({ countryList });
          })
        );
      })
    )
  );

  loadCountyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadCountyList),
      switchMap(() => {
        return combineLatest([this.postAddressService.getCountyAbbreviations()]).pipe(
          map(([countyList]) => {
            return fromReasonActions.loadCountyListSuccess({ countyList });
          })
        );
      })
    )
  );

  loadBookSettingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadBookSettingList),
      switchMap(() => {
        return combineLatest([this.bookService.getBookSettings()]).pipe(
          map(([bookSettingList]) => {
            return fromReasonActions.loadBookSettingListSuccess({ bookSettingList });
          })
        );
      })
    )
  );

  loadTranslations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadTranslations),
      switchMap((keys) => {
        return combineLatest([this.translationService.getTranslationsByKeys(keys)]).pipe(
          map(([translations]) => {
            keys.keys.forEach(key => {
              if (!translations.find(translation => translation.key === key)) {
                translations.push({ key: key });
              }
            });

            return fromReasonActions.loadTranslationsSuccess({ translations });
          })
        );
      })
    )
  );

  initServiceArticleTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initServiceArticleTreatment),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isLoadedServiceArticleTreatment)),
      ),
      switchMap(([params, serviceArticleTreatmentLoaded]) => {
        const actions: any[] = [];
        if (!serviceArticleTreatmentLoaded) {
          actions.push(fromReasonActions.loadServiceArticleTreatmentData());
        }

        return actions;
      })
    )
  );
  initMedicalHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initMedicalHistory),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isLoadedMedicalHistory)),
      ),
      switchMap(([params, isMedicalHistoryLoaded]) => {
        const actions: any[] = [];
        if (!isMedicalHistoryLoaded) {
          actions.push(fromReasonActions.loadMedicalHistoryData());
        }

        return actions;
      })
    )
  );
  initSpeciesDiagnosisData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initSpeciesDiagnosisData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isLoadedSpecisDiagnosis)),
      ),
      switchMap(([params, isLoadedSpecisDiagnosis]) => {
        const actions: any[] = [];
        if (!isLoadedSpecisDiagnosis) {
          actions.push(fromReasonActions.loadSpeciesDiagnosisData());
        }

        return actions;
      })
    )
  );
  initHexData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initHexData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isLoadedHexData)),
      ),
      switchMap(([params, isloadedHexData]) => {
        const actions: any[] = [];
        if (!isloadedHexData) {
          actions.push(fromReasonActions.loadHexData());
        }

        return actions;
      })
    )
  );
  initCountryList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initCountryList),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isCountryListLoaded)),
      ),
      switchMap(([params, isCountryListLoaded]) => {
        const actions: any[] = [];
        if (!isCountryListLoaded) {
          actions.push(fromReasonActions.loadCountryList());
        }

        return actions;
      })
    )
  );
  initCountyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initCountyList),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isCountyListLoaded)),
      ),
      switchMap(([params, isCountyListLoaded]) => {
        const actions: any[] = [];
        if (!isCountyListLoaded) {
          actions.push(fromReasonActions.loadCountyList());
        }

        return actions;
      })
    )
  );
  initBookSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initBookSettings),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isBookSettingListLoaded)),
      ),
      switchMap(([params, isBookSettingListLoaded]) => {
        const actions: any[] = [];
        if (!isBookSettingListLoaded) {
          actions.push(fromReasonActions.loadBookSettingList());
        }

        return actions;
      })
    )
  );

  loadCustomerExternalNumbersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadCustomerExternalNumbersData),
      switchMap(() => {
        return combineLatest([
          this.customerExternalNumberService.findAll6(),
        ]).pipe(
          map(([customerExternalNumbers]) => {

            return fromReasonActions.loadCustomerExternalNumbersDataSuccess({
              customerExternalNumbers: customerExternalNumbers,
            })
          })
        );
      })
    )
  );

  initCustomerExternalNumbersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initCustomerExternalNumbersData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isCustomerExternalNumbersLoaded)),
      ),
      switchMap(([params, isCustomerExternalNumbersLoaded]) => {
        const actions: any[] = [];
        if (!isCustomerExternalNumbersLoaded) {
          actions.push(fromReasonActions.loadCustomerExternalNumbersData());
        }

        return actions;
      })
    )
  );

  loadAnimalExternalNumbersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadAnimalExternalNumbersData),
      switchMap(() => {
        return combineLatest([
          this.animalExternalNumberService.findAll16(),
        ]).pipe(
          map(([animalExternalNumbers]) => {

            return fromReasonActions.loadAnimalExternalNumbersDataSuccess({
              animalExternalNumbers: animalExternalNumbers,
            })
          })
        );
      })
    )
  );
  initAnimalExternalNumbersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initAnimalExternalNumbersData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isAnimalExternalNumbersLoaded)),
      ),
      switchMap(([params, isAnimalExternalNumbersLoaded]) => {
        const actions: any[] = [];
        if (!isAnimalExternalNumbersLoaded) {
          actions.push(fromReasonActions.loadAnimalExternalNumbersData());
        }

        return actions;
      })
    )
  );
  loadAnimalDateTypesData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadAnimalDateTypesData),
      switchMap(() => {
        return combineLatest([
          this.animalDateTypeService.findAll17(),
        ]).pipe(
          map(([animalDateTypes]) => {

            return fromReasonActions.loadAnimalDateTypesDataSuccess({
              animalDateTypes: animalDateTypes,
            })
          })
        );
      })
    )
  );
  initAnimalDateTypesData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initAnimalDateTypesData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isAnimalDateTypesLoaded)),
      ),
      switchMap(([params, isAnimalDateTypesLoaded]) => {
        const actions: any[] = [];
        if (!isAnimalDateTypesLoaded) {
          actions.push(fromReasonActions.loadAnimalDateTypesData());
        }

        return actions;
      })
    )
  );
  loadCustomerCategoryGroupData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadCustomerCategoryGroupData),
      switchMap(() => {
        return combineLatest([
          this.customerCategoryGroupService.findAll8(),
        ]).pipe(
          map(([customerCategoryGroup]) => {

            return fromReasonActions.loadCustomerCategoryGroupDataSuccess({
              customerCategoryGroup: customerCategoryGroup,
            })
          })
        );
      })
    )
  );
  initCustomerCategoryGroupData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initCustomerCategoryGroupData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isCustomerCategoryGroupLoaded)),
      ),
      switchMap(([params, isCustomerCategoryGroupLoaded]) => {
        const actions: any[] = [];
        if (!isCustomerCategoryGroupLoaded) {
          actions.push(fromReasonActions.loadCustomerCategoryGroupData());
        }

        return actions;
      })
    )
  );
  loadCustomerCategoryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.loadCustomerCategoryData),
      switchMap(() => {
        return combineLatest([
          this.customerCategoryService.findAll7(),
        ]).pipe(
          map(([customerCategory]) => {

            return fromReasonActions.loadCustomerCategoryDataSuccess({
              customerCategory: customerCategory,
            })
          })
        );
      })
    )
  );
  initCustomerCategoryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReasonActions.initCustomerCategoryData),
      withLatestFrom(
        this.store$.pipe(select(fromStaticDataSelectors.isCustomerCategoryLoaded)),
      ),
      switchMap(([params, isCustomerCategoryLoaded]) => {
        const actions: any[] = [];
        if (!isCustomerCategoryLoaded) {
          actions.push(fromReasonActions.loadCustomerCategoryData());
        }

        return actions;
      })
    )
  );
}
